export const UBO_SCOPE_THRESHOLD_ONLY = "threshold_only";
export const UBO_SCOPE_THRESHOLD_AND_DIRECT = "threshold_and_direct";
export const UBO_SCOPE_ALL_UBOS = "all_ubos";

export function cleanUboValue(value) {
  return String(value || "").trim();
}

export function normalizeUboScope(value, fallback = UBO_SCOPE_THRESHOLD_ONLY) {
  const raw = cleanUboValue(value);
  if (
    raw === UBO_SCOPE_THRESHOLD_ONLY ||
    raw === UBO_SCOPE_THRESHOLD_AND_DIRECT ||
    raw === UBO_SCOPE_ALL_UBOS
  ) {
    return raw;
  }
  return fallback;
}

export function normUboPartyType(value) {
  const s = cleanUboValue(value).toLowerCase();
  if (
    s === "individual" ||
    s === "person" ||
    s === "natural person" ||
    s === "private individual"
  ) return "individual";
  if (s === "entity" || s === "company" || s === "legal person") return "entity";
  if (s === "trust") return "trust";
  return "";
}

export function uboRowIsIndividual(row) {
  const rawType = cleanUboValue(
    row?.partyType || row?.holderType || row?.shareholderEntityType || row?.type
  );
  return rawType ? normUboPartyType(rawType) === "individual" : true;
}

export function ownershipRowKey(row, fallback = "") {
  return (
    cleanUboValue(row?.treeNodeId) ||
    cleanUboValue(row?.subjectNodeId) ||
    cleanUboValue(row?._rowId) ||
    cleanUboValue(row?._docRef) ||
    cleanUboValue(row?.nodeId) ||
    cleanUboValue(row?.id) ||
    cleanUboValue(row?.name).toLowerCase() ||
    fallback
  );
}

export function dedupeOwnershipRows(rows) {
  const seen = new Set();
  const result = [];
  (rows || []).forEach((row, index) => {
    const key = ownershipRowKey(row, `row_${index}`);
    if (seen.has(key)) return;
    seen.add(key);
    result.push(row);
  });
  return result;
}

export function currentOwnershipRows(ownership) {
  return [
    ...(Array.isArray(ownership?.shareholders) ? ownership.shareholders : []),
    ...(Array.isArray(ownership?.directShareholders) ? ownership.directShareholders : []),
    ...(Array.isArray(ownership?.ubos) ? ownership.ubos : []),
    ...(Array.isArray(ownership?.controllingPersonsNatural) ? ownership.controllingPersonsNatural : []),
  ];
}

export function resolveCurrentOwnershipRow(ownership, row, fallbackIndex = 0) {
  const currentRows = currentOwnershipRows(ownership);
  const lookupKeys = [
    cleanUboValue(row?.treeNodeId),
    cleanUboValue(row?.subjectNodeId),
    cleanUboValue(row?._rowId),
  ].filter(Boolean);
  const rowName = cleanUboValue(row?.name).toLowerCase();

  const matched = currentRows.find((candidate, index) => {
    const candidateKeys = [
      cleanUboValue(candidate?.treeNodeId),
      cleanUboValue(candidate?.subjectNodeId),
      cleanUboValue(candidate?._rowId),
      ownershipRowKey(candidate, `current_${index}`),
    ].filter(Boolean);
    if (lookupKeys.some((key) => candidateKeys.includes(key))) return true;
    return rowName && cleanUboValue(candidate?.name).toLowerCase() === rowName;
  });

  return matched ? { ...row, ...matched } : { ...row, _rowId: row?._rowId || `qualified_${fallbackIndex}` };
}

export function getQualifiedIndividualUboRows(ownership) {
  const rows = Array.isArray(ownership?.qualifiedUbos)
    ? ownership.qualifiedUbos
    : Array.isArray(ownership?.uboAssessment?.qualifiedUbos)
    ? ownership.uboAssessment.qualifiedUbos
    : [];

  return dedupeOwnershipRows(
    rows
      .map((row, index) => resolveCurrentOwnershipRow(ownership, row, index))
      .filter(uboRowIsIndividual)
  );
}

export function getDirectIndividualShareholderRows(ownership) {
  const rows = [
    ...(Array.isArray(ownership?.shareholders) ? ownership.shareholders : []),
    ...(Array.isArray(ownership?.directShareholders) ? ownership.directShareholders : []),
  ];
  return dedupeOwnershipRows(rows.filter(uboRowIsIndividual));
}

export function getIndirectIndividualUboRows(ownership) {
  const rows = Array.isArray(ownership?.ubos)
    ? ownership.ubos
    : Array.isArray(ownership?.controllingPersonsNatural)
    ? ownership.controllingPersonsNatural
    : [];
  return dedupeOwnershipRows(rows.filter(uboRowIsIndividual));
}

export function getSelectedIndividualUboRows(ownership, scope) {
  const cleanScope = normalizeUboScope(scope);
  const qualifiedRows = getQualifiedIndividualUboRows(ownership);

  if (cleanScope === UBO_SCOPE_THRESHOLD_ONLY) return qualifiedRows;

  const directRows = getDirectIndividualShareholderRows(ownership);
  if (cleanScope === UBO_SCOPE_THRESHOLD_AND_DIRECT) {
    return dedupeOwnershipRows([...qualifiedRows, ...directRows]);
  }

  if (cleanScope === UBO_SCOPE_ALL_UBOS) {
    return dedupeOwnershipRows([...directRows, ...getIndirectIndividualUboRows(ownership)]);
  }

  return qualifiedRows;
}

export function ownershipCountrySignature(ownership) {
  return JSON.stringify(
    currentOwnershipRows(ownership).map((row, index) => ({
      key: ownershipRowKey(row, `row_${index}`),
      name: cleanUboValue(row?.name),
      type: cleanUboValue(row?.partyType || row?.holderType || row?.shareholderEntityType || row?.type),
      nationality: cleanUboValue(row?.nationality),
      domicile: cleanUboValue(row?.countryOfResidence || row?.domicile),
      treeNodeId: cleanUboValue(row?.treeNodeId),
      subjectNodeId: cleanUboValue(row?.subjectNodeId),
      relatedShareholderNodeId: cleanUboValue(row?.relatedShareholderNodeId),
      relatedShareholderRowId: cleanUboValue(row?.relatedShareholderRowId),
    }))
  );
}
